import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Download,
  BarChart3,
  Calendar,
  ExternalLink,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Calendar as CalendarComponent } from '@/components/ui/calendar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { format } from 'date-fns';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer
} from 'recharts';
import { TablePagination } from '@/components/TablePagination';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

import { API_URL } from '@/lib/api';

function aggregateOrdersByDay(rows) {
  const m = {};
  for (const o of rows) {
    const day = (o.created_at || '').slice(0, 10);
    if (!day) continue;
    if (!m[day]) {
      m[day] = { date: day, total_orders: 0, cod_orders: 0, online_orders: 0, revenue: 0 };
    }
    m[day].total_orders += 1;
    if (o.payment_method === 'COD') m[day].cod_orders += 1;
    else m[day].online_orders += 1;
    m[day].revenue += o.total_amount || 0;
  }
  return Object.values(m).sort((a, b) => a.date.localeCompare(b.date));
}

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-white p-3 rounded-lg shadow-lg border border-slate-200">
        <p className="text-sm font-medium text-slate-900">{label}</p>
        {payload.map((entry, index) => (
          <p key={index} className="text-sm text-slate-600">
            {entry.name}: {entry.dataKey === 'revenue' || entry.dataKey === 'total_revenue' 
              ? `₹${entry.value.toLocaleString()}` 
              : entry.value}
          </p>
        ))}
      </div>
    );
  }
  return null;
};

const Reports = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('sales');
  const [salesData, setSalesData] = useState([]);
  const [orderReportRows, setOrderReportRows] = useState([]);
  const [productData, setProductData] = useState([]);
  const [walletData, setWalletData] = useState([]);
  const [codReportStoresCount, setCodReportStoresCount] = useState(0);
  const [reportStores, setReportStores] = useState([]);
  const [reportStoreId, setReportStoreId] = useState('all');
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState({
    start_date: null,
    end_date: null
  });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const ordersChartData = useMemo(() => aggregateOrdersByDay(orderReportRows), [orderReportRows]);

  useEffect(() => {
    axios.get(`${API_URL}/stores`)
      .then((res) => {
        const rows = Array.isArray(res.data) ? res.data : [];
        setReportStores(rows.map((s) => ({ id: s.id, name: s.storeName })));
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    fetchReports();
  }, [activeTab, dateRange, reportStoreId]);

  useEffect(() => {
    setPage(1);
  }, [activeTab, dateRange, reportStoreId]);

  const fetchReports = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (dateRange.start_date) params.append('startDate', dateRange.start_date.toISOString());
      if (dateRange.end_date) params.append('endDate', dateRange.end_date.toISOString());

      if (activeTab === 'sales') {
        const res = await axios.get(`${API_URL}/reports/sales?${params}`);
        setSalesData((res.data || []).map(d => ({
          date: d.date,
          revenue: d.total ?? 0,
          total_orders: d.totalOrders ?? 0,
          cod_orders: d.codOrders ?? 0,
          online_orders: d.onlineOrders ?? 0
        })));
      } else if (activeTab === 'orders') {
        const p2 = new URLSearchParams(params);
        if (reportStoreId && reportStoreId !== 'all') p2.append('storeId', reportStoreId);
        const res = await axios.get(`${API_URL}/reports/orders?${p2}`);
        setOrderReportRows(Array.isArray(res.data) ? res.data : []);
      } else if (activeTab === 'products') {
        const params2 = new URLSearchParams();
        if (dateRange.start_date) params2.append('startDate', dateRange.start_date.toISOString());
        if (dateRange.end_date) params2.append('endDate', dateRange.end_date.toISOString());
        const res = await axios.get(`${API_URL}/reports/products?${params2}`);
        setProductData((res.data || []).map(d => ({
          product_name: d.name,
          total_orders: 0,
          total_quantity: d.quantity ?? 0,
          total_revenue: d.revenue ?? 0
        })));
      } else if (activeTab === 'cod') {
        const [w, c] = await Promise.all([
          axios.get(`${API_URL}/wallet-dues`),
          axios.get(`${API_URL}/reports/cod`),
        ]);
        setWalletData((w.data || []).map(d => ({
          store_name: d.storeName,
          total_cod_orders: d.totalCodOrders ?? 0,
          total_paid: d.totalPaid ?? 0,
          outstanding_due: d.outstandingDue ?? 0
        })));
        setCodReportStoresCount(Array.isArray(c.data) ? c.data.length : 0);
      }
    } catch (error) {
      toast.error('Failed to load report data');
    } finally {
      setLoading(false);
    }
  };

  const exportToCSV = (data, filename) => {
    if (data.length === 0) {
      toast.error('No data to export');
      return;
    }

    const headers = Object.keys(data[0]);
    const csvContent = [
      headers.join(','),
      ...data.map(row => headers.map(header => {
        const value = row[header];
        return typeof value === 'string' && value.includes(',') ? `"${value}"` : value;
      }).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `${filename}-${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    toast.success('Report exported successfully');
  };

  return (
    <div data-testid="reports-page">
      {/* Header */}
      <div className="page-header">
        <div>
          <h1 className="page-title">Reports</h1>
          <p className="text-slate-500 mt-1">Analytics and business insights</p>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
          <TabsList>
            <TabsTrigger value="sales" data-testid="sales-tab">Sales Report</TabsTrigger>
            <TabsTrigger value="orders" data-testid="orders-tab">Orders Report</TabsTrigger>
            <TabsTrigger value="products" data-testid="products-tab">Product Performance</TabsTrigger>
            <TabsTrigger value="cod" data-testid="cod-tab">COD Outstanding</TabsTrigger>
          </TabsList>

          <div className="flex flex-wrap items-center gap-2">
            {(activeTab === 'sales' || activeTab === 'orders') && (
              <>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" size="sm" data-testid="report-start-date">
                      <Calendar className="mr-2 h-4 w-4" />
                      {dateRange.start_date ? format(dateRange.start_date, 'PP') : 'Start'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="end">
                    <CalendarComponent
                      mode="single"
                      selected={dateRange.start_date}
                      onSelect={(date) => setDateRange(prev => ({ ...prev, start_date: date }))}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>

                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" size="sm" data-testid="report-end-date">
                      <Calendar className="mr-2 h-4 w-4" />
                      {dateRange.end_date ? format(dateRange.end_date, 'PP') : 'End'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="end">
                    <CalendarComponent
                      mode="single"
                      selected={dateRange.end_date}
                      onSelect={(date) => setDateRange(prev => ({ ...prev, end_date: date }))}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </>
            )}

            {activeTab === 'orders' && (
              <Select value={reportStoreId} onValueChange={setReportStoreId}>
                <SelectTrigger className="w-[180px]" data-testid="report-store-filter">
                  <SelectValue placeholder="Store" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All stores</SelectItem>
                  {reportStores.map((s) => (
                    <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}

            <Button 
              variant="outline" 
              size="sm"
              onClick={() => {
                if (activeTab === 'sales') {
                  exportToCSV(salesData, 'sales-report');
                } else if (activeTab === 'orders') {
                  const flat = orderReportRows.map((o) => ({
                    order_number: o.order_number,
                    store: o.storeName,
                    created_at: o.created_at,
                    total: o.total_amount,
                    payment_method: o.payment_method,
                    payment_status: o.payment_status,
                    order_status: o.order_status,
                  }));
                  exportToCSV(flat, 'orders-report');
                } else if (activeTab === 'products') {
                  exportToCSV(productData, 'product-performance');
                } else if (activeTab === 'cod') {
                  exportToCSV(walletData, 'cod-outstanding');
                }
              }}
              data-testid="export-csv-btn"
            >
              <Download className="w-4 h-4 mr-2" />
              Export CSV
            </Button>
          </div>
        </div>

        {/* Sales Report */}
        <TabsContent value="sales">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-950"></div>
            </div>
          ) : salesData.length === 0 ? (
            <div className="empty-state">
              <BarChart3 className="w-12 h-12 mb-3 text-slate-300" />
              <p className="text-lg font-medium">No sales data available</p>
              <p className="text-sm mt-1">Sales data will appear once orders are placed</p>
            </div>
          ) : (
            <div className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle>Revenue Trend</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-80">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={salesData.slice(0, 30)}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                        <XAxis 
                          dataKey="date" 
                          tick={{ fontSize: 11, fill: '#64748B' }}
                          tickFormatter={(value) => new Date(value).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                        />
                        <YAxis 
                          tick={{ fontSize: 11, fill: '#64748B' }}
                          tickFormatter={(value) => `₹${value}`}
                        />
                        <Tooltip content={<CustomTooltip />} />
                        <Bar dataKey="revenue" fill="#7C3AED" radius={[4, 4, 0, 0]} name="Revenue" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>

              <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead>Date</TableHead>
                      <TableHead>Total Orders</TableHead>
                      <TableHead>Revenue</TableHead>
                      <TableHead>COD Orders</TableHead>
                      <TableHead>Online Orders</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {salesData.slice((page - 1) * pageSize, page * pageSize).map((row, index) => (
                      <TableRow key={index} className="table-row-hover">
                        <TableCell>{new Date(row.date).toLocaleDateString()}</TableCell>
                        <TableCell>{row.total_orders}</TableCell>
                        <TableCell className="font-medium">₹{row.revenue.toLocaleString()}</TableCell>
                        <TableCell>{row.cod_orders}</TableCell>
                        <TableCell>{row.online_orders}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                <TablePagination
                  data={salesData}
                  page={page}
                  pageSize={pageSize}
                  onPageChange={setPage}
                  onPageSizeChange={(v) => { setPageSize(v); setPage(1); }}
                />
              </div>
            </div>
          )}
        </TabsContent>

        {/* Orders Report — GET /reports/orders */}
        <TabsContent value="orders">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-950"></div>
            </div>
          ) : orderReportRows.length === 0 ? (
            <div className="empty-state">
              <BarChart3 className="w-12 h-12 mb-3 text-slate-300" />
              <p className="text-lg font-medium">No orders in this range</p>
              <p className="text-sm mt-1 text-slate-500">Adjust dates or store filter</p>
            </div>
          ) : (
            <div className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle>Orders per day (filtered)</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-80">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={ordersChartData.slice(-30)}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                        <XAxis 
                          dataKey="date" 
                          tick={{ fontSize: 11, fill: '#64748B' }}
                          tickFormatter={(value) => new Date(value).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                        />
                        <YAxis tick={{ fontSize: 11, fill: '#64748B' }} />
                        <Tooltip content={<CustomTooltip />} />
                        <Bar dataKey="total_orders" fill="#059669" radius={[4, 4, 0, 0]} name="Orders" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>

              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <Card>
                  <CardContent className="pt-6">
                    <p className="text-sm text-slate-500">Total orders</p>
                    <p className="text-3xl font-bold">{orderReportRows.length}</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-6">
                    <p className="text-sm text-slate-500">COD</p>
                    <p className="text-3xl font-bold">{orderReportRows.filter((o) => o.payment_method === 'COD').length}</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-6">
                    <p className="text-sm text-slate-500">Online / prepaid</p>
                    <p className="text-3xl font-bold">{orderReportRows.filter((o) => o.payment_method !== 'COD').length}</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-6">
                    <p className="text-sm text-slate-500">Gross order value</p>
                    <p className="text-3xl font-bold">₹{orderReportRows.reduce((s, o) => s + (o.total_amount || 0), 0).toLocaleString()}</p>
                  </CardContent>
                </Card>
              </div>

              <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead>Order #</TableHead>
                      <TableHead>Store</TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead>Amount</TableHead>
                      <TableHead>Pay</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Open</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {orderReportRows.slice((page - 1) * pageSize, page * pageSize).map((o) => (
                      <TableRow key={o.id} className="table-row-hover">
                        <TableCell className="font-mono text-sm">{o.order_number}</TableCell>
                        <TableCell className="font-medium">{o.storeName || '—'}</TableCell>
                        <TableCell className="text-slate-500">{o.created_at ? new Date(o.created_at).toLocaleString() : '—'}</TableCell>
                        <TableCell>₹{(o.total_amount || 0).toLocaleString()}</TableCell>
                        <TableCell className="uppercase text-xs">{o.payment_method}</TableCell>
                        <TableCell className="text-xs">{o.order_status} · {o.payment_status}</TableCell>
                        <TableCell className="text-right">
                          <Button variant="ghost" size="sm" onClick={() => navigate(`/orders/${o.id}`)}>
                            <ExternalLink className="w-4 h-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                <TablePagination
                  data={orderReportRows}
                  page={page}
                  pageSize={pageSize}
                  onPageChange={setPage}
                  onPageSizeChange={(v) => { setPageSize(v); setPage(1); }}
                />
              </div>
            </div>
          )}
        </TabsContent>

        {/* Product Performance */}
        <TabsContent value="products">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-950"></div>
            </div>
          ) : productData.length === 0 ? (
            <div className="empty-state">
              <BarChart3 className="w-12 h-12 mb-3 text-slate-300" />
              <p className="text-lg font-medium">No product data available</p>
            </div>
          ) : (
            <div className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle>Top Products by Revenue</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-80">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={productData.slice(0, 10)} layout="vertical">
                        <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" horizontal={false} />
                        <XAxis 
                          type="number"
                          tick={{ fontSize: 11, fill: '#64748B' }}
                          tickFormatter={(value) => `₹${value}`}
                        />
                        <YAxis 
                          dataKey="product_name"
                          type="category"
                          tick={{ fontSize: 11, fill: '#64748B' }}
                          width={120}
                        />
                        <Tooltip content={<CustomTooltip />} />
                        <Bar dataKey="total_revenue" fill="#7C3AED" radius={[0, 4, 4, 0]} name="Revenue" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>

              <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead>Product</TableHead>
                      <TableHead>Total Orders</TableHead>
                      <TableHead>Quantity Sold</TableHead>
                      <TableHead>Revenue</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {productData.slice((page - 1) * pageSize, page * pageSize).map((row, index) => (
                      <TableRow key={index} className="table-row-hover">
                        <TableCell className="font-medium">{row.product_name}</TableCell>
                        <TableCell>{row.total_orders}</TableCell>
                        <TableCell>{row.total_quantity}</TableCell>
                        <TableCell className="font-medium">₹{row.total_revenue.toLocaleString()}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                <TablePagination
                  data={productData}
                  page={page}
                  pageSize={pageSize}
                  onPageChange={setPage}
                  onPageSizeChange={(v) => { setPageSize(v); setPage(1); }}
                />
              </div>
            </div>
          )}
        </TabsContent>

        {/* COD Outstanding */}
        <TabsContent value="cod">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-950"></div>
            </div>
          ) : walletData.length === 0 ? (
            <div className="empty-state">
              <BarChart3 className="w-12 h-12 mb-3 text-slate-300" />
              <p className="text-lg font-medium">No outstanding dues</p>
              <p className="text-sm mt-1">All COD payments are collected</p>
            </div>
          ) : (
            <div className="space-y-6">
              <p className="text-sm text-slate-500">
                Per-store detail from <code className="text-xs bg-slate-100 px-1 rounded">/wallet-dues</code>
                {' · '}
                Stores with balance due &gt; 0 (report): <strong>{codReportStoresCount}</strong>
              </p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Card>
                  <CardContent className="pt-6">
                    <p className="text-sm text-slate-500">Total Outstanding</p>
                    <p className="text-3xl font-bold text-rose-600">
                      ₹{walletData.reduce((sum, d) => sum + d.outstanding_due, 0).toLocaleString()}
                    </p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-6">
                    <p className="text-sm text-slate-500">Total Collected</p>
                    <p className="text-3xl font-bold text-emerald-600">
                      ₹{walletData.reduce((sum, d) => sum + d.total_paid, 0).toLocaleString()}
                    </p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-6">
                    <p className="text-sm text-slate-500">Stores (report /cod)</p>
                    <p className="text-3xl font-bold text-slate-800">{codReportStoresCount}</p>
                  </CardContent>
                </Card>
              </div>

              <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead>Store</TableHead>
                      <TableHead>COD Orders</TableHead>
                      <TableHead>Total Paid</TableHead>
                      <TableHead>Outstanding</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {walletData.slice((page - 1) * pageSize, page * pageSize).map((row, index) => (
                      <TableRow key={index} className="table-row-hover">
                        <TableCell className="font-medium">{row.store_name}</TableCell>
                        <TableCell>{row.total_cod_orders}</TableCell>
                        <TableCell className="text-emerald-600">₹{row.total_paid.toLocaleString()}</TableCell>
                        <TableCell className="text-rose-600 font-medium">₹{row.outstanding_due.toLocaleString()}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                <TablePagination
                  data={walletData}
                  page={page}
                  pageSize={pageSize}
                  onPageChange={setPage}
                  onPageSizeChange={(v) => { setPageSize(v); setPage(1); }}
                />
              </div>
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default Reports;
